import {
    EmptyList,
    ErrorState,
    ProtectedButton,
    Table,
    TableSkeleton,
} from '@src/components';
import { DeleteModal } from '@src/components/DeleteModal/DeleteModal';
import { NEW_ROLE_FULL_PATH } from '@src/constants/URLS.const';
import { usePageTitle } from '@src/contexts';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RenderColumns } from './columns';
import { useRolesList } from './hooks';
import { PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useDisplayListLogic, useLocalStorageValue } from '@src/hooks';

export default function RolesList() {
    const { t } = useTranslation();
    const { setBreadcrumbs } = usePageTitle();
    const navigate = useNavigate();

    useEffect(() => {
        setBreadcrumbs([{ title: t('pages.users.title') }]);
    }, []);

    const storageKey = 'rolesHiddenColumns';
    const [hiddenColumns, setHiddenColumns] = useLocalStorageValue<string[]>(
        storageKey,
        []
    );

    const {
        data,
        isLoading,
        isFetching,
        isDeleting,
        isError,
        searchText,
        openRoleDeleteModal,
        selectedRole,
        sortBy,
        sortOrder,
        confirmDelete,
        handleActionClick,
        handleSearchChange,
        handleChangePagination,
        setOpenRoleDeleteModal,
        handleSortChange,
    } = useRolesList();

    const columns = RenderColumns({
        t,
        sorterState: { sortBy, sortOrder, onSortChange: handleSortChange },
        hiddenColumns,
        setHiddenColumns,
        onActionClick: handleActionClick,
    });

    const { showTable, showEmptyState: originalShowEmptyState } =
        useDisplayListLogic({
            data: data?.data.items ?? [],
            originalData: data?.data.items ?? [],
            isLoading,
            isFetching,
            isError,
            searchText,
        });

    const showErrorState = isError && !isLoading && !isFetching;

    const hasErrorOrLoading =
        isError || (isLoading && !data?.data?.items?.length);
    const showEmptyState = originalShowEmptyState && !hasErrorOrLoading;

    const totalBaseColumns =
        columns.filter((c) => c.key !== 'actions').length +
        hiddenColumns.length;
    const allHidden = hiddenColumns.length >= totalBaseColumns;

    return (
        <>
            {isLoading && <TableSkeleton />}

            {showTable && !showErrorState && (
                <Table
                    title={
                        <div className="flex gap-2 items-center">
                            <span>
                                {`You have ${data?.data.pagination.total ?? 0} ${t('pages.role.title')}`}
                            </span>
                        </div>
                    }
                    searchPlaceholder={t('general.searchMinChars')}
                    hasFilter={false}
                    scroll={{ x: 1 }}
                    loading={isLoading || isFetching}
                    columns={columns}
                    actions={
                        <ProtectedButton
                            module="RoleManagement"
                            labelKey="pages.role.buttons.newRole"
                            icon={<PlusIcon width={16} height={16} />}
                            onClick={() => navigate(NEW_ROLE_FULL_PATH)}
                        />
                    }
                    dataSource={data?.data?.items || []}
                    onPageChange={handleChangePagination}
                    paginationProps={{
                        total: data?.data?.pagination?.total ?? 0,
                        current: data?.data?.pagination?.currentPage,
                        pageSize: data?.data?.pagination?.perPage,
                    }}
                    searchValue={searchText}
                    onSearch={handleSearchChange}
                    onRowClick={(record) =>
                        handleActionClick('details', record)
                    }
                    allColumnsHidden={allHidden}
                    onShowAllColumns={() => setHiddenColumns([])}
                />
            )}

            {showEmptyState && !showErrorState && (
                <EmptyList
                    message={t('general.emptyListMessage', {
                        name: t('pages.role.title'),
                    })}
                >
                    <ProtectedButton
                        module="RoleManagement"
                        labelKey="pages.role.buttons.newRole"
                        icon={<PlusIcon width={16} height={16} />}
                        onClick={() => navigate(NEW_ROLE_FULL_PATH)}
                    />
                </EmptyList>
            )}

            {showErrorState && (
                <ErrorState
                    message={t('errors.loadingTrouble', {
                        name: t('pages.role.title').toLowerCase(),
                    })}
                />
            )}

            {openRoleDeleteModal && selectedRole && (
                <DeleteModal
                    isOpen={openRoleDeleteModal}
                    isDeleting={isDeleting}
                    icon={<UserGroupIcon className="w-6 h-6" />}
                    pageTitle={t('pages.role.title')}
                    itemName={selectedRole.name}
                    deleteMessage={
                        selectedRole?.name
                            ? t('keyTerms.deleteMessage', {
                                  name: selectedRole.name,
                              })
                            : undefined
                    }
                    onClose={() => setOpenRoleDeleteModal(false)}
                    onDelete={confirmDelete}
                />
            )}
        </>
    );
}
